import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  formatAmount,
  formatPhoneEmail,
} from '../futures/futuresOrderConstants';
import {
  inputDateTimeToQueryApi,
  toInputDateTime,
} from '../futures/futuresRoundConstants';

/** 提現方式（TransactionType 子集） */
export const WITHDRAW_TYPE_OPTIONS = [
  { value: 'WITHDRAWAL_MEMBER', label: '用戶提現' },
  { value: 'WITHDRAWAL_ONLINE', label: '線上提款' },
  { value: 'WITHDRAWAL_OFFLINE', label: '線下提現' },
  { value: 'WITHDRAWAL_SYSTEM', label: '系統下分' },
];

export const WITHDRAW_TYPE_LABELS = Object.fromEntries(
  WITHDRAW_TYPE_OPTIONS.map((item) => [item.value, item.label])
);

/** 審核狀態 */
export const WITHDRAW_STATUS_OPTIONS = [
  { value: 'PENDING', label: '待處理' },
  { value: 'PROCESSING', label: '審核中' },
  { value: 'WAITING_CALLBACK', label: '待回調' },
  { value: 'SUCCESS', label: '成功' },
  { value: 'FAIL', label: '失敗' },
  { value: 'REJECT', label: '拒絕' },
  { value: 'TIMEOUT', label: '超時' },
  { value: 'EXCEPTION', label: '異常' },
];

export const WITHDRAW_STATUS_LABELS = Object.fromEntries(
  WITHDRAW_STATUS_OPTIONS.map((item) => [item.value, item.label])
);

export const FIRST_WITHDRAW_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'true', label: '是' },
  { value: 'false', label: '否' },
];

export const FIRST_BINDING_OPTIONS = [
  { value: '', label: '全部' },
  { value: '1', label: '首綁' },
  { value: '2', label: '修改' },
];

export const FIRST_BINDING_LABELS = {
  1: '首綁',
  2: '修改',
};

export { MEMBER_ROLE_LABELS, MEMBER_ROLE_OPTIONS, formatAmount, formatPhoneEmail };

const MAX_WITHDRAW_QUERY_DAYS = 31;

export function defaultWithdrawApplyTimeRange() {
  const now = new Date();
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);

  const to = new Date(now);
  to.setDate(to.getDate() + 1);
  to.setHours(0, 0, 0, 0);

  return {
    createTimeFromInput: toInputDateTime(from),
    createTimeToInput: toInputDateTime(to),
  };
}

function calendarDaysBetween(fromInput, toInput) {
  const fromDate = fromInput.slice(0, 10);
  const toDate = toInput.slice(0, 10);
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

export function validateWithdrawTimeRange(fromInput, toInput, label = '申請時間') {
  if (!fromInput && !toInput) {
    return '';
  }
  if ((fromInput && !toInput) || (!fromInput && toInput)) {
    return `${label}起迄需同時填寫`;
  }
  const fromApi = inputDateTimeToQueryApi(fromInput);
  const toApi = inputDateTimeToQueryApi(toInput);
  if (fromApi > toApi) {
    return `${label}起不可晚於迄`;
  }
  const days = calendarDaysBetween(fromInput, toInput);
  if (days > MAX_WITHDRAW_QUERY_DAYS) {
    return `${label}跨度不可超過 ${MAX_WITHDRAW_QUERY_DAYS} 天`;
  }
  return '';
}

export function buildWithdrawPageParams(pageNo, filters) {
  const params = {
    pageNo,
    pageSize: 15,
  };

  if (filters.thirdRecordCode?.trim()) {
    params.thirdRecordCode = filters.thirdRecordCode.trim();
  }
  if (filters.type) {
    params.type = filters.type;
  }
  if (filters.thirdName?.trim()) {
    params.thirdName = filters.thirdName.trim();
  }
  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.roleId) {
    params.roleId = Number(filters.roleId);
  }
  if (filters.topInviterId?.trim()) {
    params.topInviterId = Number(filters.topInviterId);
  }
  if (filters.inviterId?.trim()) {
    params.inviterId = Number(filters.inviterId);
  }
  if (filters.memberId?.trim()) {
    params.memberId = Number(filters.memberId);
  }
  if (filters.phoneEmail?.trim()) {
    params.phoneEmail = filters.phoneEmail.trim();
  }
  if (filters.currencyName?.trim()) {
    params.currencyName = filters.currencyName.trim();
  }
  if (filters.minWithdrawAmount?.trim()) {
    params.minWithdrawAmount = filters.minWithdrawAmount.trim();
  }
  if (filters.maxWithdrawAmount?.trim()) {
    params.maxWithdrawAmount = filters.maxWithdrawAmount.trim();
  }
  if (filters.departmentId?.trim()) {
    params.departmentId = Number(filters.departmentId);
  }
  if (filters.id?.trim()) {
    params.id = Number(filters.id);
  }
  if (filters.address?.trim()) {
    params.address = filters.address.trim();
  }
  if (filters.firstWithdraw === 'true') {
    params.firstWithdraw = true;
  } else if (filters.firstWithdraw === 'false') {
    params.firstWithdraw = false;
  }
  if (filters.behaviorNumber?.trim()) {
    params.behaviorNumber = Number(filters.behaviorNumber);
  }
  if (filters.firstBindingOrModified) {
    params.firstBindingOrModified = Number(filters.firstBindingOrModified);
  }
  if (filters.createTimeFromInput) {
    params.createTimeBegin = inputDateTimeToQueryApi(filters.createTimeFromInput);
  }
  if (filters.createTimeToInput) {
    params.createTimeEnd = inputDateTimeToQueryApi(filters.createTimeToInput);
  }
  if (filters.callbackTimeFromInput) {
    params.callbackDateBegin = inputDateTimeToQueryApi(filters.callbackTimeFromInput);
  }
  if (filters.callbackTimeToInput) {
    params.callbackDateEnd = inputDateTimeToQueryApi(filters.callbackTimeToInput);
  }

  return params;
}

export function formatWithdrawType(type) {
  if (!type) {
    return '—';
  }
  const code = typeof type === 'string' ? type : type?.name || type?.code || type;
  return WITHDRAW_TYPE_LABELS[code] || code;
}

export function formatWithdrawStatus(status) {
  if (!status) {
    return '—';
  }
  const code = typeof status === 'string' ? status : status?.name || status?.code || status;
  return WITHDRAW_STATUS_LABELS[code] || code;
}

export function formatOptionalId(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

export function formatFirstWithdraw(behaviorRemark) {
  return behaviorRemark === 'FIRST' ? '是' : '否';
}

export function formatFirstBinding(value) {
  if (value == null || value === '') {
    return '—';
  }
  return FIRST_BINDING_LABELS[value] || value;
}

export function withdrawStatusClassName(status) {
  const code = String(typeof status === 'string' ? status : status?.name || status || '').toUpperCase();
  if (code === 'SUCCESS') {
    return 'text-profit';
  }
  if (code === 'FAIL' || code === 'REJECT' || code === 'TIMEOUT' || code === 'EXCEPTION') {
    return 'text-loss';
  }
  if (code === 'PENDING' || code === 'PROCESSING' || code === 'WAITING_CALLBACK') {
    return 'text-pending';
  }
  return '';
}

/** 後台通過審核：人工出款 / 三方出款 */
export const WITHDRAW_PASS_METHOD = {
  MANUAL: 'MANUAL',
  THIRD_PARTY: 'THIRD_PARTY',
};

export const WITHDRAW_PASS_METHOD_OPTIONS = [
  { value: WITHDRAW_PASS_METHOD.MANUAL, label: '人工提款' },
  { value: WITHDRAW_PASS_METHOD.THIRD_PARTY, label: '三方提款' },
];

/**
 * 依提現單 channelId 或幣種解析可出款的三方通道與商戶。
 * @param {Array} merchantChannels adminApi.listMerchantChannels()
 * @param {{ channelId?: number, currencyName?: string }} row
 */
export function resolveWithdrawPayoutChannel(merchantChannels, row) {
  const channels = Array.isArray(merchantChannels) ? merchantChannels : [];
  const channelId = row?.channelId != null ? Number(row.channelId) : null;
  const currency = String(row?.currencyName || '').trim().toUpperCase();

  let matched = [];
  if (channelId != null && !Number.isNaN(channelId)) {
    const byId = channels.find((c) => Number(c.id) === channelId);
    if (byId && byId.isWithdrawal) {
      matched = [byId];
    }
  }
  if (matched.length === 0 && currency) {
    matched = channels.filter(
      (c) =>
        c.isWithdrawal &&
        String(c.currencyType || '').trim().toUpperCase() === currency,
    );
  }

  const channel = matched[0] || null;
  const merchants = (channel?.supportedMerchants || []).filter((m) => m.isEnabled !== false);
  return { channel, merchants, matchedChannels: matched };
}

export function withdrawRowStatusCode(row) {
  return String(typeof row?.status === 'string' ? row.status : row?.status?.name || row?.status || '').toUpperCase();
}

export function canWithdrawLock(row) {
  return !row?.isLock && withdrawRowStatusCode(row) === 'PENDING';
}

export function canWithdrawUnlock(row) {
  const status = withdrawRowStatusCode(row);
  return Boolean(row?.isLock) && (status === 'PENDING' || status === 'PROCESSING');
}

export function canWithdrawPassReject(row) {
  return Boolean(row?.isLock) && withdrawRowStatusCode(row) === 'PROCESSING';
}
